import { CheckCircleFilled, CloseCircleFilled } from '@ant-design/icons';
import { Button, message, Modal, Row, Skeleton, Space } from 'antd';
import { useEffect } from 'react';
import {
  ChargeType,
  FareStatusType,
  FareType,
} from '../../../../../typesFinancial';
import { usePatchChargeMutation } from '../../../../../services/chargeApi';

type FareAllToArchiveModalProps = {
  showModal: boolean;
  setShowModal: (showModal: boolean) => void;
  selectedCharge: ChargeType;
};

// O backend arquiva as tarifas em BILLING, REVERSED e SEND_ERROR e deixa as
// demais de fora sem erro, porque arquivá-las seria errado: a que está em
// PROCESSING pertence ao worker até o desfecho chegar, e a faturada precisa ser
// estornada antes. O que ele não faz é contar isso, então a contagem é daqui,
// a partir da lista que a resposta devolve.
const SKIPPED_GROUPS: {
  status: FareStatusType[];
  singular: string;
  plural: string;
}[] = [
  {
    status: ['PROCESSING'],
    singular: 'está em transmissão para o RM',
    plural: 'estão em transmissão para o RM',
  },
  {
    status: ['BILLED', 'REBILLED'],
    singular: 'já foi faturada; estorne o demonstrativo antes de arquivar',
    plural: 'já foram faturadas; estorne o demonstrativo antes de arquivar',
  },
];

function buildArchiveFeedback(fares: FareType[]): {
  type: 'success' | 'warning';
  text: string;
} {
  const skipped = fares.filter((fare) => fare.status !== 'ARCHIVED');

  if (!skipped.length) {
    return { type: 'success', text: 'Tarifas arquivadas com sucesso' };
  }

  const groupedStatus = SKIPPED_GROUPS.flatMap((group) => group.status);
  const clauses = SKIPPED_GROUPS.map((group) => ({
    group,
    count: skipped.filter(
      (fare) => fare.status && group.status.includes(fare.status)
    ).length,
  }))
    .filter(({ count }) => count > 0)
    .map(({ group, count }) =>
      count === 1 ? `1 ${group.singular}` : `${count} ${group.plural}`
    );

  // Rede de segurança: status fora dos grupos acima não deve sobrar hoje, mas
  // se sobrar a contagem precisa fechar, senão a mensagem mente por omissão.
  const ungrouped = skipped.filter(
    (fare) => !fare.status || !groupedStatus.includes(fare.status)
  ).length;
  if (ungrouped) {
    clauses.push(
      ungrouped === 1
        ? '1 não pôde ser arquivada'
        : `${ungrouped} não puderam ser arquivadas`
    );
  }

  const archived = fares.length - skipped.length;
  const header = archived
    ? `${archived} de ${fares.length} tarifas arquivadas.`
    : 'Nenhuma tarifa foi arquivada.';

  return { type: 'warning', text: `${header} ${clauses.join('. ')}.` };
}

export function FareAllToArchiveModal(props: FareAllToArchiveModalProps) {
  const { showModal, setShowModal, selectedCharge } = props;

  function closeModal() {
    setShowModal(false);
  }

  const [
    patchCharge,
    {
      data: chargeFares,
      isSuccess: isSuccessPatchCharge,
      isLoading: isLoadingPatchCharge,
    },
  ] = usePatchChargeMutation();

  useEffect(() => {
    if (isSuccessPatchCharge) {
      const feedback = buildArchiveFeedback(chargeFares || []);
      message[feedback.type](feedback.text);
    }
    closeModal();
  }, [isSuccessPatchCharge]);

  async function handleFareToArchive() {
    if (selectedCharge && selectedCharge.id) {
      patchCharge({
        action: 'archive_statement',
        id: selectedCharge.id,
      });
    }
  }

  return (
    <Modal
      visible={showModal}
      title={<>Confirmar arquivamento de tarifa</>}
      className="TOSPrimaryModal"
      onCancel={closeModal}
      width={620}
      footer={null}
    >
      <>
        <h2>
          As tarifas dessa fatura serão arquivadas. Tarifas em transmissão ou já
          faturadas não entram. Deseja continuar?
        </h2>
        <Row justify="end">
          <Space size={12}>
            <Button
              onClick={() => closeModal()}
              disabled={isLoadingPatchCharge}
            >
              Cancelar
            </Button>
            <Button
              type="primary"
              danger
              onClick={() => handleFareToArchive()}
              loading={isLoadingPatchCharge}
            >
              Sim, Arquivar
            </Button>
          </Space>
        </Row>
      </>
    </Modal>
  );
}
